export type TransceiverKind = 'audio' | 'video';

export type TransceiverSpec = {
  kind: TransceiverKind;
  direction: RTCRtpTransceiverDirection;
};

/**
 * Ensures a given set of audio/video transceivers exist on an
 * RTCPeerConnection BEFORE the first offer is created.
 *
 * This guarantees the initial SDP offer / answer contains the expected
 * m=audio / m=video sections, removing the reliance on a second
 * negotiationneeded round-trip to add them later — which is race-prone
 * in peer-lite's handler and is the root cause of the rare
 * "blue screen" / no-video WebRTC bug observed in marketing-demo
 * (see plan: fix_webrtc_blue_screen).
 *
 * Idempotent: will NOT create a duplicate transceiver if one already
 * exists for the same media kind (e.g. on a consumed pre-warmed
 * connection that already had transceivers added at pre-warm time).
 */
export class TransceiverInitializer {
  public ensure(pc: RTCPeerConnection | null, specs: TransceiverSpec[]): void {
    if (!pc) return;
    try {
      const existing = pc.getTransceivers();
      for (const spec of specs) {
        if (this.hasTransceiverForKind(existing, spec.kind)) continue;
        pc.addTransceiver(spec.kind, { direction: spec.direction });
      }
    } catch (err) {
      console.warn('[TransceiverInitializer] Failed to ensure transceivers:', err);
    }
  }

  private hasTransceiverForKind(
    transceivers: readonly RTCRtpTransceiver[],
    kind: TransceiverKind,
  ): boolean {
    return transceivers.some((t) => {
      const receiverKind = t.receiver?.track?.kind ?? null;
      const senderKind = t.sender?.track?.kind ?? null;
      return receiverKind === kind || senderKind === kind;
    });
  }
}
